import { useEffect, useState } from 'react';
import { Box, Paper, Typography, Alert } from '@mui/material';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import { Formik, Form, FormikHelpers } from 'formik';
import { ChangePaymentDate } from './types';
import * as Yup from 'yup';
import FinanceAgreement from './components/FinanceAgreement';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import { useNavigate } from 'react-router';
import { addOrdinalSuffix } from '@acorn-portal/shared/src/utils';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import API_POLICY_TOKEN from '@acorn-portal/shared/src/services/managedPaymentsInstance';
import FullPageLoadingSplash from '@acorn-portal/shared/src/components/loading/FullPageLoadingSplash';
import { setCanChangePaymentDay } from '@acorn-portal/shared/src/store/managedPayments.slice';
import { PAYMENT_SUMMARY_ERRORS } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import { PaymentClient } from '../../services/managedPaymentsApi';
import { ROUTE_PATHS } from '../../utils/Routes';
import Dropdown from '../../components/dropdown';
import { differenceInDays, format } from 'date-fns';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

export function getNotification(currentPaymentDay: number, selectedPaymentDay: number): string {
	const currentDate = new Date();
	const currentYear = currentDate.getFullYear();
	const currentMonth = currentDate.getMonth();

	const currentPaymentDate = new Date(currentYear, currentMonth, currentPaymentDay);
	const selectedPaymentDate = new Date(currentYear, currentMonth, selectedPaymentDay);
	if (format(currentPaymentDate, 'dd') === format(selectedPaymentDate, 'dd')) {
		return '';
	}
	if (selectedPaymentDay > 28) {
		return 'For months with fewer days than your selected date, the payment will be taken on the last day of the month.';
	}

	// If the current date falls within 10 days before the selected payment date
	const difference = differenceInDays(selectedPaymentDate, currentDate);
	if (difference >= 0 && difference <= 10) {
		return "The change of your direct debit date will not affect this month's payment, but will take effect in subsequent months.";
	}

	return '';
}

const ChangeOfPaymentDate = () => {
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [isSuccessFlow, setIsSuccessFlow] = useState<boolean>(false);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const managedPaymentsDetails = useAppSelector((redux) => redux.managedPayments);
	const { paymentDay } = managedPaymentsDetails;
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const schema = fullPolicy.scheme;
	const isNonFinancePolicy = fullPolicy.nonFinancePolicy;

	const paymentDates = paymentDay.availableCollectionDays ?? [];

	const paymentDays = paymentDates.map((day) => day);

	const sortedPaymentDays = [...paymentDays].sort((a, b) => a - b);

	const initialValues: ChangePaymentDate = {
		collectionDay: paymentDay.paymentDay ?? 0,
		confirmAffordability: false,
	};

	const [selectedDay, setSelectedDay] = useState<number | undefined>(paymentDay.paymentDay);

	const [notification, setNotification] = useState('');

	useEffect(() => {
		if (paymentDay.canChangePaymentDay === false && !isSuccessFlow) {
			navigate(ROUTE_PATHS.home);
		}
	}, [paymentDay.canChangePaymentDay, isSuccessFlow, navigate]);

	useEffect(() => {
		if (selectedDay) {
			const newNotification = getNotification(paymentDay.paymentDay ?? 0, selectedDay ?? 0);
			setNotification(newNotification);
		}
	}, [selectedDay, paymentDay]);

	const handleDateChangeSubmit = async (_values: ChangePaymentDate, helpers: FormikHelpers<ChangePaymentDate>) => {
		setSubmitting(true);
		if (selectedDay !== undefined && selectedDay !== paymentDay.paymentDay) {
			try {
				const managePaymentClient = new PaymentClient(undefined, API_POLICY_TOKEN);
				const changeDay = await managePaymentClient.changePaymentDay({
					collectionDay: selectedDay,
				});
				setIsSuccessFlow(true);
				dispatch(setCanChangePaymentDay(false));
				setSubmitting(false);
				navigate(ROUTE_PATHS.paymentManagementChangePaymentDateSuccess, {
					replace: true,
					state: {
						paymentDay: changeDay.newPaymentDay,
						paymentDate: changeDay.nextPaymentCollection,
					},
				});
			} catch (e) {
				console.error(e);
				helpers.setFormikState((prevState) => ({
					...prevState,
					values: {
						...prevState.values,
						confirmAffordability: initialValues.confirmAffordability,
					},
					errors: {
						...prevState.errors,
						confirmAffordability: PAYMENT_SUMMARY_ERRORS.detailsCorrect,
						collectionDay: PAYMENT_SUMMARY_ERRORS.collectionDay,
					},
					touched: {
						...prevState.touched,
						confirmAffordability: true,
						collectionDay: true,
					},
				}));
				setSubmitting(false);
			}
		}
	};

	const validationSchema = Yup.object().shape({
		confirmAffordability: Yup.boolean().oneOf([true], PAYMENT_SUMMARY_ERRORS.detailsCorrect),
		collectionDay: Yup.number()
			.required(PAYMENT_SUMMARY_ERRORS.collectionDay)
			.notOneOf([paymentDay.paymentDay], 'New payment day has not been selected'),
	});

	return (
		<FullPageLoadingSplash
			loading={paymentDay === undefined || submitting}
			message={submitting ? 'Changing Payment Day' : 'Retrieving Payment Details...'}
		>
			<Paper
				elevation={1}
				sx={{
					borderRadius: '8px',
				}}
			>
				<JourneyCardLayout pageTitle='Change Payment Date' loading={false}>
					<Box
						sx={{
							pb: 4,
							margin: 2,
							textAlign: 'center',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'center',
						}}
					>
						<Formik initialValues={initialValues} onSubmit={handleDateChangeSubmit} validationSchema={validationSchema}>
							{(formik) => {
								return (
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											textAlign: 'left',
											px: { lg: '200px', sm: 'unset' },
										}}
									>
										<Form>
											<FormikLogErrors />
											<Box>
												<Typography>
													{' '}
													Your current preferred collection day is{' '}
													<b>{addOrdinalSuffix(paymentDay.paymentDay)} of each month.</b>
												</Typography>
											</Box>
											<Box sx={{ py: '20px' }}>
												<Typography>
													Please select a new preferred payment day from the available options below.
												</Typography>
											</Box>
											<Box sx={{ pt: '20px', textAlign: 'center' }}>
												<Dropdown
													label=''
													name='collectionDay'
													value={selectedDay?.toString()}
													onChange={(value) => {
														formik.setFieldValue('collectionDay', Number(value));
														setSelectedDay(Number(value));
													}}
													data={sortedPaymentDays.map((day) => ({
														value: day.toString(),
														label: day.toString(),
													}))}
												/>
											</Box>
											{(notification || formik.errors.collectionDay) && (
												<Alert
													variant='standard'
													severity='info'
													sx={{
														width: '100%',
														borderRadius: 2,
														marginY: { xs: 2 },
														marginBottom: { md: 0 },
														textAlign: 'left',
														alignItems: 'center',
														margin: 'auto',
													}}
												>
													<Typography>{notification || formik.errors.collectionDay}</Typography>
												</Alert>
											)}

											<FinanceAgreement isNonFinancePolicy={isNonFinancePolicy} scheme={schema} />
											<ActionButtons>
												<BackButton onClick={() => navigate(-1)}>Cancel</BackButton>
												<ContinueButton type='submit'>Submit</ContinueButton>
											</ActionButtons>
										</Form>
									</Box>
								);
							}}
						</Formik>
					</Box>
				</JourneyCardLayout>
			</Paper>
		</FullPageLoadingSplash>
	);
};
export default ChangeOfPaymentDate;
